import { useEffect, useState, useCallback } from "react";
import Utils from "../../utils/utils";
import { useAppContext } from "../ApplicationServicesContext";
import Permission from "../../services/permission";
import UI_PERMISSIONS_NAME from "../../config/uiPermissionsNames";
import PubSub from "../../services/PubSub";
import CONFIG from "../../config/config";
import { checkDeploymentIsUnlocked, getUnlockedDeploymentsFromStorage, getDeploymentStageFromStorage } from "./deploymentLockHooks";

const DISABLED_DOMAIN_ALLOWED_PERMISSIONS = [
    UI_PERMISSIONS_NAME.ViewUsers,
    UI_PERMISSIONS_NAME.ViewDomain,
    UI_PERMISSIONS_NAME.ViewEnvironments,
    UI_PERMISSIONS_NAME.ViewDeployment,
    UI_PERMISSIONS_NAME.ViewBuildtime,
    UI_PERMISSIONS_NAME.ViewRuntime,
    UI_PERMISSIONS_NAME.ViewSyncLogs,
];

const DEPLOYMENT_EDIT_PERMISSIONS = [
    UI_PERMISSIONS_NAME.CreateDeployment,
    UI_PERMISSIONS_NAME.EditDeployment,
    UI_PERMISSIONS_NAME.EditBuildtime,
    UI_PERMISSIONS_NAME.EditRuntime,
    UI_PERMISSIONS_NAME.Notifications,
];

const getFilteredPermissions = ({ domain, environment, deployment, name, permissions }) => {
    if (deployment) {
        return permissions?.filter((perm) =>
            perm.deployment === deployment &&
            perm.name === name
        );
    }

    if (environment) {
        return permissions?.filter((perm) =>
            perm.environment === environment &&
            perm.name === name
        );
    }

    return permissions?.filter((perm) =>
        perm.domain === domain.toString() &&
        perm.name === name
    );
};

const hasUiPermission = ({ domain, environment, deployment, name, domainObj, bypassStageLockCheck = false }) => {
    const user = JSON.parse(window.sessionStorage.getItem("user")) || {};

    if (!bypassStageLockCheck && deployment && !DISABLED_DOMAIN_ALLOWED_PERMISSIONS.includes(name)) {
        const stage = getDeploymentStageFromStorage(deployment);

        // If deployment is in locked stage, check if it's unlocked
        if (CONFIG.LOCKED_STAGES.includes(stage)) {
            const isUnlocked = checkDeploymentIsUnlocked(deployment);

            // If not unlocked, deny permission.
            if (!isUnlocked) {
                return false;
            }
        }
    }

    if (user.appAdmin) {
        return true;
    }

    // Use domainObj by default, fallback to session storage domain, if session storage domain doesn't agree with domain (param)
    const workingDomainObj = domainObj || Utils.getDomain();
    if (workingDomainObj._id !== domain) {
        return false;
    }
    const domainDisabled = workingDomainObj.enabled === false;
    const isDomainAdmin = user.adminForDomains?.includes(workingDomainObj?._id);

    if (isDomainAdmin) {
        // For disabled domains, domain admins have limited permissions.
        if (domainDisabled) {
            return DISABLED_DOMAIN_ALLOWED_PERMISSIONS.includes(name);
        }
        // For enabled domains, domain admins have full permissions.
        return true;
    }

    const filteredPermissions = getFilteredPermissions({
        domain,
        environment,
        deployment,
        name,
        permissions: user.uiPermissions
    });

    return filteredPermissions && filteredPermissions.length > 0;
}

const getDeploymentPermission = ({ domain, deployments, name, bypassStageLockCheck }) => {
    const permissions = {};
    if (domain && name && deployments && deployments.length > 0) {
        deployments.forEach((deployment) => {
            permissions[deployment._id] = hasUiPermission({ domain, environment: deployment.environment, deployment: deployment._id, name, bypassStageLockCheck });
        });
    }

    return permissions;
}

const useHasPermission = ({ domain, environment, deployment, name, bypassStageLockCheck = false }) => {
    const { domain: contextDomain } = useAppContext();
    domain = domain || contextDomain._id;
    const [permissions, setPermissions] = useState(false);
    const [unlockedDeployments, setUnlockedDeployments] = useState(null);

    useEffect(() => {
        const id = PubSub.subscribe("refresh-unlocked-deployments", () => {
            const stored = getUnlockedDeploymentsFromStorage();
            setUnlockedDeployments(stored);
        });

        return () => {
            PubSub.unsubscribe("refresh-unlocked-deployments", id);
        };
    }, [])

    useEffect(() => {
        if (domain && name) {
            let allowed = hasUiPermission({ domain, environment, deployment, name, bypassStageLockCheck });
            setPermissions(allowed);
        }
    }, [domain, environment, deployment, name, unlockedDeployments, bypassStageLockCheck]);

    return permissions;
};

const useHasAnyPermission = ({ domain, environment, deployment, names }) => {
    const { domain: contextDomain } = useAppContext();
    domain = domain || contextDomain._id;
    const [permissions, setPermissions] = useState(false);
    const [unlockedDeployments, setUnlockedDeployments] = useState(null);

    useEffect(() => {
        const id = PubSub.subscribe("refresh-unlocked-deployments", () => {
            const stored = getUnlockedDeploymentsFromStorage();
            setUnlockedDeployments(stored);
        });

        return () => {
            PubSub.unsubscribe("refresh-unlocked-deployments", id);
        };
    }, [])

    useEffect(() => {
        if (domain && names && names.length > 0) {
            let allowed = false;
            names.forEach(name => {
                let allowedPermission = hasUiPermission({ domain, environment, deployment, name });
                allowed = allowed || allowedPermission;
            })
            setPermissions(allowed);
        }
    }, [domain, environment, deployment, names, unlockedDeployments]);

    return permissions;
};

const usePermissionsInDomain = ({ domainId }) => {
    const { domain: contextDomain } = useAppContext();
    domainId = domainId || contextDomain._id;
    const [allPermissions, setAllPermissions] = useState([]);

    const getPermissions = useCallback(async (permissionsForDomainId) => {
        const response = await Permission.getUiPermissionsForDomain(permissionsForDomainId);
        if (!response.error) {
            setAllPermissions(response.data);
        }
    }, []);

    useEffect(() => {
        getPermissions(domainId);
    }, [domainId, getPermissions]);

    return allPermissions;
};

const useDeploymentEditPermissions = (deployments) => {
    const { domain } = useAppContext();
    const [permissions, setPermissions] = useState({});

    useEffect(() => {
        if (!deployments || deployments.length === 0) {
            setPermissions({});
            return;
        }

        const permissionsByDeployment = Object.fromEntries(
            DEPLOYMENT_EDIT_PERMISSIONS.map(permissionName => [
                permissionName,
                getDeploymentPermission({
                    domain: domain._id,
                    deployments: deployments,
                    name: permissionName,
                    bypassStageLockCheck: true
                })
            ])
        );

        const result = {};
        deployments.forEach(deployment => {
            const deploymentPermissions = {};
            DEPLOYMENT_EDIT_PERMISSIONS.forEach(permissionName => {
                deploymentPermissions[permissionName] = permissionsByDeployment[permissionName][deployment._id];
            });

            result[deployment._id] = Object.values(deploymentPermissions).some(Boolean);
        });

        setPermissions(result);
    }, [deployments, domain]);

    return permissions;
};

export {
    hasUiPermission,
    getDeploymentPermission,
    useHasPermission,
    useHasAnyPermission,
    usePermissionsInDomain,
    useDeploymentEditPermissions,
    DEPLOYMENT_EDIT_PERMISSIONS
} 